'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import {
  useCommerceSource,
  useBlockPart,
  tx,
  PortalLink,
  type ProductListing,
} from '@akademi/site-builder-runtime';
import { Input, SelectField, Pagination } from '@akademi/ui';
import {
  SectionShell,
  type PaddingBox,
  EmptyState,
  SkeletonRow,
  AdapterPlaceholder,
} from '@akademi/site-builder-blocks';
import { ProductThumb } from './ProductThumb';
import { ProductPrice } from './ProductPrice';
import { AvailabilityBadge } from './AvailabilityBadge';
import { readProductQuery, patchProductParams, type ProductQueryScope } from './productQuery';
import { SavedListPicker, hasBuyingListsCapability, type SavedListPickerSource } from './SavedListPicker';

/**
 * Listing grid of {@code ProductCard}s, driven by a
 * {@link ProductListingQuery} read from the page URL + this block's static
 * scope props — reads through {@code useCommerceSource()} exactly like
 * every other host-coupled block; never calls {@code fetch} itself. Renders
 * an inline placeholder when no provider is mounted (backend not wired
 * yet — a normal state, not an error) and treats an empty result / a
 * rejected call the same way (empty state, never a thrown error reaching
 * the page).
 *
 * <h3>Pairs with {@code FacetPanel}</h3>
 * Both blocks read the same URL query state (see {@code productQuery.ts})
 * independently — drop a {@code FacetPanel} anywhere on the same page (with
 * the same {@code categorySlugPath} / {@code brandSlug} / {@code
 * productType} scope) and it narrows this grid with no prop wiring between
 * the two, the same pattern {@code CourseGrid} + {@code CategoryTree} use.
 *
 * <h3>Links never build a URL from a slug</h3>
 * Every card links to {@link ProductCard.publicPath} verbatim — a kit lives
 * at {@code /kits/{slug}}, a plain product at {@code /p/{slug}}, and only
 * the backend knows which.
 */
export interface ProductGridProps {
  title?: string;
  /** Pin this grid to one category's slug path (e.g. {@code "kits/solar"}).
   *  Leave unset for "the default listing for this channel". */
  categorySlugPath?: string;
  brandSlug?: string;
  productType?: string;
  featured?: boolean;
  pageSize?: number;
  columns?: 'auto' | '2' | '3' | '4' | '5';
  showSearch?: boolean;
  searchPlaceholder?: string;
  showSort?: boolean;
  sortOptions?: { value: string; label: string }[];
  defaultSort?: string;
  showResultCount?: boolean;
  emptyTitle?: string;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
  titleFontSize?: string;
  enableSavedLists?: boolean;
  savedListSource?: SavedListPickerSource;
  savedListScopeKey?: string;
}

const DEFAULT_PAGE_SIZE = 12;
const MIN_PAGE_SIZE = 1;
const MAX_PAGE_SIZE = 48;
const SEARCH_DEBOUNCE_MS = 350;

const DEFAULT_SORT_OPTIONS = [
  { value: 'relevance', labelKey: 'commerce.grid.sort.relevance' },
  { value: 'newest', labelKey: 'commerce.grid.sort.newest' },
  { value: 'price_asc', labelKey: 'commerce.grid.sort.priceAsc' },
  { value: 'price_desc', labelKey: 'commerce.grid.sort.priceDesc' },
];

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function ProductGrid({
  title,
  categorySlugPath,
  brandSlug,
  productType,
  featured,
  pageSize: rawPageSize,
  columns = 'auto',
  showSearch = true,
  searchPlaceholder,
  showSort = true,
  sortOptions,
  defaultSort,
  showResultCount = true,
  emptyTitle,
  paddingOuter,
  paddingInner,
  titleFontSize,
  enableSavedLists,
  savedListSource,
  savedListScopeKey,
}: ProductGridProps) {
  const commerce = useCommerceSource();
  const listSource = savedListSource ?? (hasBuyingListsCapability(commerce) ? commerce.buyingLists : undefined);
  const listScopeKey = savedListScopeKey ?? listSource?.getScopeKey?.();
  const showSavedLists = enableSavedLists ?? Boolean(listSource && listScopeKey);
  const part = useBlockPart();
  const { t } = useTranslation('site-builder-blocks');
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const pageSize = clamp(
    Math.trunc(rawPageSize ?? DEFAULT_PAGE_SIZE),
    MIN_PAGE_SIZE,
    MAX_PAGE_SIZE,
  );
  const scope: ProductQueryScope = { categorySlugPath, brandSlug, productType, featured };
  const resolvedSortOptions =
    sortOptions ?? DEFAULT_SORT_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }));

  const query = readProductQuery(searchParams, scope);
  const sortValue = query.sort ?? defaultSort ?? resolvedSortOptions[0]?.value;
  const effectiveQuery = { ...query, sort: sortValue, size: pageSize };

  const queryKey = JSON.stringify(effectiveQuery);

  const [listing, setListing] = useState<ProductListing | null>(null);
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  useEffect(() => {
    if (!commerce) return;
    let cancelled = false;
    setStatus('loading');
    commerce
      .listProducts(effectiveQuery)
      .then((result) => {
        if (cancelled) return;
        setListing(result);
        setStatus('loaded');
      })
      .catch(() => {
        if (cancelled) return;
        setListing(null);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
    // effectiveQuery is derived from queryKey; re-run only when it changes.
  }, [commerce, queryKey]);

  const [searchInput, setSearchInput] = useState(query.q ?? '');
  useEffect(() => {
    setSearchInput(query.q ?? '');
  }, [query.q]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  function updateParams(patch: Record<string, string | undefined>) {
    const params = patchProductParams(searchParams, patch);
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }

  function onSearchChange(value: string) {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateParams({ q: value.trim() || undefined });
    }, SEARCH_DEBOUNCE_MS);
  }

  if (!commerce) {
    return (
      <SectionShell
        title={title}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
        titleFontSize={titleFontSize}
      >
        <AdapterPlaceholder
          blockType="ProductGrid"
          adapterName="CommerceSource"
          reason={t('commerce.adapter.reason')}
        />
      </SectionShell>
    );
  }

  const isInitialLoad = status === 'loading' && listing === null;
  const searchPlaceholderText = searchPlaceholder
    ? tx(searchPlaceholder)
    : t('commerce.grid.searchPlaceholder');
  const emptyTitleText = emptyTitle ? tx(emptyTitle) : t('commerce.grid.empty.title');

  const toolbar =
    showSearch || showSort ? (
      <div {...part('filter')} style={toolbarStyle}>
        {showSearch && (
          <div style={{ flex: '0 1 320px', minWidth: 180, maxWidth: 320 }}>
            <Input
              {...part('search')}
              type="search"
              value={searchInput}
              placeholder={searchPlaceholderText}
              onChange={(e) => onSearchChange(e.target.value)}
              aria-label={searchPlaceholderText}
            />
          </div>
        )}
        {showSort && resolvedSortOptions.length > 0 && (
          <SelectField
            value={sortValue}
            onChange={(v) => updateParams({ sort: v })}
            options={resolvedSortOptions}
            ariaLabel={t('commerce.grid.sortLabel')}
            placement="bottom-end"
          />
        )}
      </div>
    ) : null;

  const resultCount =
    showResultCount && listing ? (
      <div style={countStyle}>{t('commerce.grid.resultCount', { count: listing.total })}</div>
    ) : null;

  let body: React.ReactNode;
  if (isInitialLoad) {
    body = <SkeletonRow count={pageSize} layout="grid" minCardWidth={240} />;
  } else if (status === 'error') {
    body = (
      <EmptyState title={t('commerce.grid.error.title')} hint={t('commerce.grid.error.hint')} />
    );
  } else if (!listing || listing.items.length === 0) {
    body = <EmptyState title={emptyTitleText} hint={t('commerce.grid.empty.hint')} />;
  } else {
    body = (
      <>
        {listing.redirectTo && (
          <div style={redirectNoteStyle}>
            <PortalLink href={listing.redirectTo}>{t('commerce.grid.redirectNote')}</PortalLink>
          </div>
        )}
        <div
          {...part('list')}
          className="commerce-product-grid"
          style={{
            display: 'grid',
            gridTemplateColumns:
              columns === 'auto'
                ? 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))'
                : `repeat(${columns}, minmax(0, 1fr))`,
          }}
        >
          {listing.items.map((item) => (
            <div key={item.id} className="commerce-product-card" style={cardLinkStyle} {...part('item')}>
            <PortalLink
              key={item.id}
              href={item.publicPath}
              style={{ ...cardLinkStyle, flex: 1 }}
            >
              <div className="commerce-product-card__media">
                <ProductThumb
                  image={item.heroImage}
                  aspect="4 / 5"
                  sizes="(max-width: 640px) 100vw, 320px"
                  style={{ borderRadius: 0 }}
                  seed={item.publicPath ?? String(item.id)}
                />
                <div className="commerce-product-card__badges">
                  <AvailabilityBadge availability={item.availability} />
                  {item.listPrice && item.listPrice.amount !== item.price?.amount ? (
                    <span className="commerce-product-card__sale">Offer</span>
                  ) : null}
                </div>
              </div>
              <div className="commerce-product-card__body">
                {item.brandName && <div style={brandStyle}>{item.brandName}</div>}
                <div style={cardTitleStyle}>{item.title}</div>
                <div className="commerce-product-card__footer">
                  <ProductPrice price={item.price} listPrice={item.listPrice} size="sm" />
                  <span className="commerce-product-card__arrow" aria-hidden="true">
                    →
                  </span>
                </div>
              </div>
            </PortalLink>
            {showSavedLists && listSource && listScopeKey && <div style={{ padding: '0 1rem 1rem' }}>
              <SavedListPicker key={`${listScopeKey}:${item.id}`} source={listSource} scopeKey={listScopeKey} product={{ id: String(item.id), title: item.title }} />
            </div>}
            </div>
          ))}
        </div>

        {listing.total > pageSize && (
          <div style={{ marginTop: 24 }}>
            <Pagination
              totalItems={listing.total}
              currentPage={listing.page || 1}
              pageSize={pageSize}
              onPageChange={(p) => updateParams({ page: p > 1 ? String(p) : undefined })}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <SectionShell
      title={title}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
      titleFontSize={titleFontSize}
      maxWidth="full"
    >
      <style dangerouslySetInnerHTML={{ __html: PRODUCT_GRID_CSS }} />
      {resultCount}
      {toolbar}
      {body}
    </SectionShell>
  );
}

const toolbarStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 'var(--space-6, 24px)',
};

const countStyle: CSSProperties = {
  marginBottom: 12,
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-tertiary, #6a6f73)',
};

const redirectNoteStyle: CSSProperties = {
  marginBottom: 16,
  padding: '10px 14px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-surface-muted, #f4f4f5)',
  fontSize: 'var(--font-size-sm, 13px)',
};

const cardLinkStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  color: 'inherit',
  textDecoration: 'none',
};

const brandStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs, 12px)',
  color: 'var(--color-text-tertiary, #71717a)',
  textTransform: 'uppercase',
  letterSpacing: 0,
  fontWeight: 700,
};

const cardTitleStyle: CSSProperties = {
  marginTop: 6,
  fontSize: 'var(--font-size-md, 16px)',
  fontWeight: 650,
  color: 'var(--color-text-primary, #18181b)',
  lineHeight: 1.35,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const PRODUCT_GRID_CSS = `
.commerce-product-grid { gap: clamp(1rem, 2.2vw, 2rem); }
.commerce-product-card {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--color-border-subtle, rgba(0,0,0,.08));
  border-radius: var(--radius-card, 12px);
  background: var(--color-surface-card, #fff);
  box-shadow: var(--shadow-sm, 0 2px 8px rgba(0,0,0,.06));
  transition: transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease;
}
.commerce-product-card:hover {
  transform: translateY(-4px);
  border-color: color-mix(in srgb, var(--color-brand-500, #2563eb) 34%, transparent);
  box-shadow: var(--shadow-lg, 0 16px 36px rgba(0,0,0,.13));
}
.commerce-product-card:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--color-brand-500, #2563eb) 38%, transparent);
  outline-offset: 3px;
}
.commerce-product-card__media { position: relative; overflow: hidden; background: var(--color-surface-muted, #f4f4f5); }
.commerce-product-card__media img,
.commerce-product-card__media > div { transition: transform 500ms cubic-bezier(.2,.7,.2,1); }
.commerce-product-card:hover .commerce-product-card__media img,
.commerce-product-card:hover .commerce-product-card__media > div { transform: scale(1.035); }
.commerce-product-card__badges {
  position: absolute;
  inset: .75rem .75rem auto;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: .5rem;
  pointer-events: none;
}
.commerce-product-card__badges > * { box-shadow: 0 2px 10px rgba(0,0,0,.1); backdrop-filter: blur(8px); }
.commerce-product-card__sale {
  order: -1;
  padding: .35rem .55rem;
  border-radius: 999px;
  background: var(--color-brand-700, #1d4ed8);
  color: var(--color-text-on-brand, #fff);
  font-size: .6875rem;
  font-weight: 800;
  line-height: 1;
}
.commerce-product-card__body { display: flex; flex: 1; flex-direction: column; padding: 1rem 1rem 1.125rem; }
.commerce-product-card__footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: .75rem;
  margin-top: auto;
  padding-top: 1.25rem;
}
.commerce-product-card__arrow {
  display: grid;
  flex: none;
  width: 2rem;
  height: 2rem;
  place-items: center;
  border-radius: 999px;
  background: var(--color-surface-muted, #f4f4f5);
  color: var(--color-text-primary, #18181b);
  font-size: 1.125rem;
  transition: background 180ms ease, color 180ms ease, transform 180ms ease;
}
.commerce-product-card:hover .commerce-product-card__arrow {
  transform: translateX(2px);
  background: var(--color-brand-600, #2563eb);
  color: var(--color-text-on-brand, #fff);
}
@media (prefers-reduced-motion: reduce) {
  .commerce-product-card,
  .commerce-product-card__media img,
  .commerce-product-card__media > div,
  .commerce-product-card__arrow { transition: none; }
  .commerce-product-card:hover,
  .commerce-product-card:hover .commerce-product-card__media img,
  .commerce-product-card:hover .commerce-product-card__media > div { transform: none; }
}
`;
